import { useRef, useState } from "react";
import { ApiError, errorMessage } from "@/app/api";
import { importPrefixes, type PrefixImportResult } from "@/app/master";
import { useSave } from "@/app/useSave";
import { Alert, Button, Code, Drawer, KeyValue, Table, TableWrap, Th } from "@/ui";

/** 导入文件的表头，与服务端一致（docs/04 第 2 节）。 */
const HEADER = "prefix,country,mcc,mnc,operator_name";

/** 错误表最多列这么多行：再多也改不过来，先按前面的改。 */
const MAX_SHOWN_ERRORS = 100;

/** 读文件全文。用 FileReader 而不是 File.text()：测试环境（jsdom）没有后者。 */
function readText(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error ?? new Error("读取文件失败"));
    r.readAsText(f);
  });
}

/** 号码前缀批量导入（#138）：选 CSV 文件先预览新增、更新与错误行，没有错误时确认整批写入；已存在的前缀按文件更新，启停不动。 */
export function PrefixImport({ onClose }: { onClose: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<PrefixImportResult | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [checking, setChecking] = useState(false);
  const { save, saving, error, warning } = useSave(() => importPrefixes(file!.text, true), {
    invalidate: ["prefixes"],
    message: (out) => `已导入：新增 ${out.created} 条，更新 ${out.updated} 条`, // 用实际写入的计数，不用预览时的
    onSaved: onClose,
  });

  const choose = async (f: File) => {
    const text = await readText(f);
    setFile({ name: f.name, text });
    setPreview(null);
    setPreviewError("");
    setChecking(true);
    try {
      setPreview(await importPrefixes(text, false));
    } catch (e) {
      // 有错误行时 400 的响应体就是预览结果，照样列出来；其他错误（表头不对、空文件）显示文案
      const body = e instanceof ApiError ? (e.body as PrefixImportResult | undefined) : undefined;
      if (body?.errors?.length) setPreview(body);
      else setPreviewError(errorMessage(e));
    } finally {
      setChecking(false);
    }
  };

  const errors = preview?.errors ?? [];
  const changes = preview ? preview.created + preview.updated : 0;
  const ready = !!preview && errors.length === 0 && changes > 0;
  return (
    <Drawer open title="批量导入号码前缀" onClose={onClose}
      footer={warning ? <Button onClick={onClose}>关闭</Button> : <><Button onClick={onClose}>取消</Button><Button variant="primary" disabled={!ready || saving} onClick={() => save(undefined)}>导入</Button></>}>
      <p className="ax-secondary" style={{ margin: 0, fontSize: 13, lineHeight: "18px" }}>
        CSV 文件，第一行是表头 <Code>{HEADER}</Code>。已存在的前缀按文件更新国家、MCC、MNC 与运营商，启停状态不变；新前缀默认启用。先预览，没有错误才能导入；有任何一行出错整批不写。
      </p>
      <input ref={input} type="file" accept=".csv,text/csv" hidden aria-label="CSV 文件" onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); e.target.value = ""; }} />
      <div className="ax-row">
        <Button onClick={() => input.current?.click()} disabled={checking || saving}>{file ? "重新选择文件" : "选择 CSV 文件"}</Button>
        {file && <span className="ax-secondary">{file.name}</span>}
      </div>
      {checking && <p className="ax-secondary">正在检查…</p>}
      {previewError && <Alert tone="danger" title="文件不能导入">{previewError}</Alert>}
      {error && <Alert tone="danger" title="导入失败">{error}</Alert>}
      {warning && <Alert tone="warning" title="已导入，后续同步未完成">{warning}</Alert>}
      {preview && (
        <KeyValue items={[
          { label: "数据行", value: preview.rows }, { label: "新增", value: preview.created }, { label: "更新", value: preview.updated },
          { label: "不变", value: preview.unchanged }, { label: "有错的行", value: errors.length },
        ]} />
      )}
      {preview && errors.length === 0 && changes === 0 && <Alert title="没有需要导入的变化">文件里的前缀与库里完全相同。</Alert>}
      {errors.length > 0 && (
        <>
          <Alert tone="danger" title={`${errors.length} 行有错，整批不能导入`}>改好文件后重新选择。{errors.length > MAX_SHOWN_ERRORS && `下面只列前 ${MAX_SHOWN_ERRORS} 行。`}</Alert>
          <TableWrap>
            <Table dense>
              <thead><tr><Th numeric w={80}>行号</Th><Th>问题</Th></tr></thead>
              <tbody>
                {errors.slice(0, MAX_SHOWN_ERRORS).map((e) => <tr key={e.line}><td className="ax-num">{e.line}</td><td style={{ whiteSpace: "normal" }}>{e.message}</td></tr>)}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </Drawer>
  );
}
